"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, CheckCircle2, Loader2, Eye, EyeOff, Shield } from "lucide-react";

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Google Ads conversion tracking
const trackSignupConversion = () => {
  if (typeof window === "undefined") {
    console.log("[Google Ads] Not in browser environment");
    return;
  }

  console.log("[Google Ads] Attempting to track signup conversion...");
  console.log("[Google Ads] window.gtag exists:", !!window.gtag);
  console.log("[Google Ads] window.dataLayer exists:", !!window.dataLayer);

  // Method 1: Use gtag function if available
  if (window.gtag) {
    console.log("[Google Ads] Firing conversion via gtag()");
    window.gtag("event", "conversion", {
      send_to: "AW-11371837109/_OitCOjHwukbELXtwa4q",
    });
    return;
  }

  // Method 2: Push directly to dataLayer as fallback
  if (window.dataLayer) {
    console.log("[Google Ads] Firing conversion via dataLayer.push()");
    window.dataLayer.push({
      event: "conversion",
      send_to: "AW-11371837109/_OitCOjHwukbELXtwa4q",
    });
    return;
  }

  // Method 3: Retry after a short delay if gtag isn't ready yet
  console.log("[Google Ads] gtag not ready, retrying in 500ms...");
  setTimeout(() => {
    if (window.gtag) {
      console.log("[Google Ads] Firing conversion via gtag() (delayed)");
      window.gtag("event", "conversion", {
        send_to: "AW-11371837109/_OitCOjHwukbELXtwa4q",
      });
    } else {
      console.error("[Google Ads] gtag still not available after delay");
    }
  }, 500);
};

// Validation schema — simplified to reduce signup friction
const signupSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Password must contain uppercase, lowercase, and a number"
    ),
});

type SignupFormData = z.infer<typeof signupSchema>;

export default function SignupPage() {
  const router = useRouter();
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const t = useTranslations("auth.signup");
  const tAuth = useTranslations("auth");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    // Honeypot check - if filled, silently reject (bots fill hidden fields)
    if (honeypot) {
      // Fake success to not alert bots
      setSuccess(t("successTitle"));
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // Create user and organization
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          password: data.password,
          website: honeypot, // Send honeypot to server for double-check
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "Failed to create account");
        return;
      }

      // Show success message - user needs to verify email
      setSuccess(result.message || t("successTitle"));

      // Track Google Ads conversion on successful signup
      trackSignupConversion();
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Signup error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        {/* Logo/Brand */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            JobWright
          </h1>
          <p className="mt-3 text-xl font-semibold text-gray-900 dark:text-white">
            {t("freeTrialHeadline")}
          </p>
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            {t("noCreditCard")}
          </span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("title")}</CardTitle>
            <CardDescription>
              {t("descriptionSimplified")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Success Alert */}
              {success && (
                <div className="rounded-md bg-success/10 p-4 text-success dark:bg-green-900/20 dark:text-green-400 border-2 border-success/30">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-semibold text-base">{t("successTitle")}</p>
                      <p className="font-bold text-sm bg-success/20 p-2 rounded">
                        {t("successImportant")}
                      </p>
                      <p className="text-sm">
                        {t("successMessage")}
                      </p>
                      <p className="text-xs mt-2">
                        {t("didntReceiveEmail")}{" "}
                        <Link href="/resend-verification" className="underline hover:no-underline font-medium">
                          {t("resendVerification")}
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Alert */}
              {error && (
                <div className="flex items-center gap-2 rounded-md bg-error/10 p-3 text-sm text-error dark:bg-red-900/20 dark:text-error">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}

              {/* Streamlined form fields — no sections, just fields */}
              <Input
                {...register("name")}
                type="text"
                label={t("fullNameLabel")}
                placeholder={t("fullNamePlaceholder")}
                error={errors.name?.message}
                disabled={isLoading || !!success}
                autoComplete="name"
                autoFocus
              />

              <Input
                {...register("email")}
                type="email"
                label={t("emailLabel")}
                placeholder={t("emailPlaceholder")}
                error={errors.email?.message}
                disabled={isLoading || !!success}
                autoComplete="email"
              />

              <div className="relative">
                <Input
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  label={t("passwordLabel")}
                  placeholder={t("passwordPlaceholder")}
                  error={errors.password?.message}
                  disabled={isLoading || !!success}
                  autoComplete="new-password"
                  helperText={t("passwordHelperTextSimplified")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Honeypot field - hidden from humans, bots will fill it */}
              <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Submit Button with free trial badge */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !!success}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("submitting")}
                  </>
                ) : success ? (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t("accountCreated")}
                  </>
                ) : (
                  t("submitButtonTrial")
                )}
              </Button>

              {/* Free • No CC badge below button */}
              {!success && (
                <p className="text-center text-xs font-medium text-green-700 dark:text-green-400">
                  {t("freeNoCCBadge")}
                </p>
              )}

              {/* Trust badges */}
              {!success && (
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-gray-400 dark:text-gray-500">
                  <span className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {t("trustEncryption")}
                  </span>
                  <span>•</span>
                  <span>{t("trustNoCreditCard")}</span>
                  <span>•</span>
                  <span>{t("trustCancelAnytime")}</span>
                </div>
              )}
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              {t("hasAccount")}{" "}
              <Link
                href="/login"
                className="font-medium text-[var(--color-primary,#3b82f6)] hover:underline"
              >
                {t("signIn")}
              </Link>
            </div>
          </CardFooter>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          {tAuth("termsAgreementSignup")}
        </p>
      </div>
    </div>
  );
}
